import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocationClients, useRefreshClients } from '../../../features/locations/use-clients'
import { useLocation } from '../../../features/locations/use-location'
import { useIdOrCode } from '../../use-id-or-code'
import { Button, EmptyRow, Inline, SubCrumb, td, th } from './controls'
import { Badge, Card, mono, Notice, PageHeader, SkeletonCards } from './ui'

const utc = (iso: string | null): string => {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`
}

function useElapsed(active: boolean): number {
  const [s, setS] = useState(0)
  useEffect(() => {
    if (!active) {
      setS(0)
      return
    }
    const t = setInterval(() => setS((x) => x + 1), 1000)
    return () => clearInterval(t)
  }, [active])
  return s
}

export function PorcelainClients() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const [noIpOnly, setNoIpOnly] = useState(false)
  const clients = useLocationClients(idOrCode, { noIpOnly })
  const refresh = useRefreshClients()
  const elapsed = useElapsed(refresh.isPending)

  const header = (
    <PageHeader
      eyebrow={<SubCrumb idOrCode={idOrCode} section="network" page="Clients" />}
      title="Clients"
      subtitle="Devices recently seen on this store's network."
    />
  )

  if (clients.isLoading || location.isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={2} />
      </section>
    )
  if (clients.error || !clients.data)
    return (
      <section>
        {header}
        <Notice
          tone="error"
          title="We couldn't load clients"
          body={clients.error ? apiErrorMessage(clients.error) : 'Check your connection and try again in a moment.'}
        />
      </section>
    )

  const { clients: rows, lastRefreshedAt, isStale } = clients.data
  const aptosCode = location.data?.aptosStoreCode ?? location.data?.code ?? idOrCode

  return (
    <section>
      {header}
      <div className="mt-8">
        <Card
          title={`${rows.length} client${rows.length === 1 ? '' : 's'}`}
          aside={
            <div className="flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[var(--color-muted)]">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-accent)]"
                  checked={noIpOnly}
                  onChange={(e) => setNoIpOnly(e.target.checked)}
                />
                No IP only
              </label>
              <Button disabled={refresh.isPending} onClick={() => refresh.mutate({ aptosCodes: [aptosCode] })}>
                {refresh.isPending ? `Refreshing ${elapsed}s` : 'Refresh'}
              </Button>
            </div>
          }
        >
          <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] px-5 py-3 text-[13px]">
            <span className="text-[var(--color-muted)]">Last refreshed {utc(lastRefreshedAt)}</span>
            {isStale ? <Badge label="Stale" tone="warn" /> : null}
            {refresh.isPending ? (
              <output className="text-[var(--color-muted)]">
                Collecting from the network. This usually takes under a minute.
              </output>
            ) : null}
            {refresh.error ? <Inline tone="bad">{apiErrorMessage(refresh.error)}</Inline> : null}
            {refresh.data?.status === 'complete' ? <Inline tone="ok">Refreshed.</Inline> : null}
            {refresh.data?.status === 'running' ? (
              <Inline tone="muted">Still collecting. Results will appear when it finishes.</Inline>
            ) : null}
            {refresh.data && (refresh.data.status === 'errored' || refresh.data.status === 'terminated') ? (
              <Inline tone="bad">
                Refresh {refresh.data.status}
                {refresh.data.error ? `: ${refresh.data.error}` : '.'}
              </Inline>
            ) : null}
          </div>
          {rows.length === 0 ? (
            <EmptyRow>{noIpOnly ? 'Every client has an IP address.' : 'No clients have been seen yet.'}</EmptyRow>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className={th}>Hostname</th>
                    <th className={th}>MAC</th>
                    <th className={th}>IP</th>
                    <th className={th}>VLAN</th>
                    <th className={th}>Vendor</th>
                    <th className={th}>Seen in</th>
                    <th className={th}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.mac} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                      <td className={`${td} font-medium`}>{c.hostname ?? '-'}</td>
                      <td className={td} style={mono}>
                        {c.mac}
                      </td>
                      <td className={td} style={mono}>
                        {c.ip ?? <Badge label="No IP" tone="warn" />}
                      </td>
                      <td className={td} style={mono}>
                        {c.vlan ?? '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`}>{c.vendor || '-'}</td>
                      <td className={`${td} text-[13px] text-[var(--color-muted)]`}>{c.seenIn.join(', ') || '-'}</td>
                      <td className={`${td} text-[13px] text-[var(--color-muted)]`}>{utc(c.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </section>
  )
}
